import { Anchor, Group, Stack, Text } from '@mantine/core';
import {
  HORIZONTAL_PLACEMENTS,
  VERTICAL_PLACEMENTS,
  isAutoCardPlacement,
  resolvedCardPlacement,
} from '../constants/bannerChrome';
import classes from './CardPlacementPicker.module.css';

// How tall the card glyph is drawn, as a share of the cell it sits in. A bar
// spans the viewport and is short; a pop-up is the one block that is big enough
// to read as a card. Nothing here is to scale — the author sets the real size
// two fields away — it is just enough shape that the three display types do not
// all look like the same block.
const CARD_SHAPE = {
  popup: { height: '70%', width: '85%' },
  flyout: { height: '55%', width: '80%' },
  bar: { height: '42%', width: '100%' },
};

const VERTICAL_LABEL = { top: 'Top', center: 'Middle', bottom: 'Bottom' };
const HORIZONTAL_LABEL = { left: 'left', center: 'center', right: 'right' };

// What a cell is called, on screen and to a screen reader. Deliberately not the
// `Left`/`Right`/`Top`/`Bottom` of the legacy Position control: two controls on
// this form can set a flyout's side, and they are easier to tell apart when
// they do not answer to the same name.
export const placementLabel = (vertical, horizontal) =>
  horizontal ? `${VERTICAL_LABEL[vertical]} ${HORIZONTAL_LABEL[horizontal]}` : VERTICAL_LABEL[vertical];

const DISPLAY_TYPE_NAME = { popup: 'pop-up', flyout: 'flyout', bar: 'bar' };

// The flex rules that pin the glyph to the corner its cell stands for, so the
// card hugs the edge of the viewport rather than floating in the middle of a
// ninth of it.
const ALIGN = { top: 'flex-start', center: 'center', bottom: 'flex-end' };
const JUSTIFY = { left: 'flex-start', center: 'center', right: 'flex-end' };

// Placing a block used to be two dropdowns — `Vertical position` and
// `Horizontal position` — plus two free-text offsets, four controls in a column
// of eighteen, none of which showed the author what they were about to get.
// This is the same four values as one gesture: a screen, nine places on it,
// and the card drawn where the SDK will actually draw it, `auto` included.
//
// It writes `cssStyles` keys and nothing else; the caller decides what a chosen
// axis means for `displayPosition` (see `handlePositionChange`), because that
// mirror depends on which model the block belongs to and this control has no
// business knowing.
const CardPlacementPicker = ({ displayType, displayPosition, cssStyles, onChange }) => {
  const { vertical, horizontal } = resolvedCardPlacement({ displayType, displayPosition, cssStyles });
  const auto = isAutoCardPlacement({ displayType, cssStyles });
  // A bar spans the viewport: it has no horizontal axis, so its grid is a
  // single column of three full-width strips rather than nine cells.
  const columns = displayType === 'bar' ? [null] : HORIZONTAL_PLACEMENTS;
  const shape = CARD_SHAPE[displayType] ?? CARD_SHAPE.popup;

  const place = (cellVertical, cellHorizontal) => {
    const change = { cardPositionVertical: cellVertical };

    if (cellHorizontal) {
      change.cardPositionHorizontal = cellHorizontal;
    }

    onChange(change);
  };

  const reset = () => {
    const change = { cardPositionVertical: 'auto' };

    if (displayType !== 'bar') {
      change.cardPositionHorizontal = 'auto';
    }

    onChange(change);
  };

  return (
    <Stack gap={6}>
      <div className={classes.viewport}>
        <div className={classes.chrome}>
          <span className={classes.dot} />
          <span className={classes.dot} />
          <span className={classes.dot} />
        </div>
        <div
          className={classes.grid}
          role="group"
          aria-label="Card placement"
          style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)`, gridTemplateRows: 'repeat(3, 1fr)' }}
        >
          {VERTICAL_PLACEMENTS.map((cellVertical) =>
            columns.map((cellHorizontal) => {
              const selected = cellVertical === vertical && cellHorizontal === horizontal;

              return (
                <button
                  type="button"
                  key={`${cellVertical}-${cellHorizontal ?? 'full'}`}
                  className={classes.cell}
                  aria-pressed={selected}
                  aria-label={placementLabel(cellVertical, cellHorizontal)}
                  style={{ alignItems: ALIGN[cellVertical], justifyContent: JUSTIFY[cellHorizontal ?? 'center'] }}
                  onClick={() => place(cellVertical, cellHorizontal)}
                >
                  <span
                    className={classes.card}
                    style={shape}
                    data-selected={selected || undefined}
                    data-auto={selected && auto ? true : undefined}
                  />
                </button>
              );
            })
          )}
        </div>
      </div>
      {auto ? (
        <Text size="xs" c="dimmed">
          Auto — {placementLabel(vertical, horizontal).toLowerCase()} for this {DISPLAY_TYPE_NAME[displayType]}. Pick a
          spot to override it.
        </Text>
      ) : (
        <Group gap="xs">
          <Text size="xs" c="dimmed">
            {placementLabel(vertical, horizontal)}
          </Text>
          <Anchor component="button" type="button" size="xs" onClick={reset}>
            Reset to auto
          </Anchor>
        </Group>
      )}
    </Stack>
  );
};

export default CardPlacementPicker;
